import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Droplets, Recycle, Gauge, CloudRain, Waves, Sprout, Activity, Sun, Wrench, Compass,
  Award, Brain, Lightbulb, ShieldCheck, Handshake, Anchor, Heart,
} from "lucide-react";
import { BRAND, JOURNEY, STANDARDS_BADGES } from "../lib/content";
import SEO from "../components/SEO";
import CEOMessage from "../components/hidroverse/CEOMessage";

const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.2, 0.7, 0.2, 1] } } };
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.06, delayChildren: 0.05 } } };

const AT_A_GLANCE = [
  { val: "2016", lbl: "Founded · R&D" },
  { val: "1,500+", lbl: "Sites under SLA" },
  { val: "13", lbl: "Disciplines" },
  { val: "97%+", lbl: "Uptime SLA" },
];

const DISCIPLINES = [
  { icon: Droplets, accent: "#1FB6C1", eyebrow: "Treatment", title: "Purification", lede: "Multi-stage trains turn any feed into SANS 241 potable. The discipline behind every glass of water on the site.", brag: "SANS 241", bragLbl: "Compliant" },
  { icon: Recycle, accent: "#42954B", eyebrow: "Treatment", title: "Wastewater", lede: "Aerobic biological treatment with reuse-ready tertiary polishing. Discreet. Reliable. Audit-defensible.", brag: "Reuse-ready", bragLbl: "Effluent" },
  { icon: Gauge, accent: "#0C2A40", eyebrow: "Hydraulics", title: "Pumps & Pressure", lede: "VSD-driven duty + standby sets. Whisper-rated, redundant, sized to peak demand — not catalogue averages.", brag: "<55 dBA", bragLbl: "At 1 m" },
  { icon: CloudRain, accent: "#1D6A8C", eyebrow: "Source", title: "Harvesting", lede: "Borehole, surface, rain and coastal intakes. Yield-tested sizing, partitioned storage, drought-resilient.", brag: "Multi-source", bragLbl: "Blending" },
  { icon: Waves, accent: "#1FB6C1", eyebrow: "Recreation", title: "Pool & Spa", lede: "AFM® filtration, salt chlorination, UV-C polish. The crystal-clear wellness register guests notice.", brag: "≈ 4 µm", bragLbl: "Filtration" },
  { icon: Sprout, accent: "#42954B", eyebrow: "Field", title: "Irrigation", lede: "Drip, sub-surface, smart ET-zero zones. The water economy turf, citrus and ornamentals demand.", brag: "Up to 95%", bragLbl: "Efficiency" },
  { icon: Activity, accent: "#0C2A40", eyebrow: "Data", title: "Monitoring", lede: "LoRaWAN telemetry, pressure-zone alerting, audit-grade logging. Know about a leak before guests do.", brag: "Real-time", bragLbl: "Telemetry" },
  { icon: Sun, accent: "#1D6A8C", eyebrow: "Power", title: "Solar Cleaning", lede: "Autonomous robotic cleaning to maintain PV output in dusty environments. Off-peak, minimal water.", brag: "+15%", bragLbl: "Yield" },
  { icon: Compass, accent: "#1D6A8C", eyebrow: "Project", title: "Wet-Services PM", lede: "Single accountable supplier from architect's brief to commissioning. No procurement afterthoughts.", brag: "One signature", bragLbl: "Whole site" },
  { icon: Wrench, accent: "#1D6A8C", eyebrow: "Service", title: "SLA & Maintenance", lede: "Contracted critical-response clause, monthly SANAS sampling, consumables included. Lifetime cover, not just defect period.", brag: "SLA", bragLbl: "Lifetime cover" },
];

const VALUE_ICONS = [Award, Brain, Lightbulb, ShieldCheck, Handshake, Anchor, Heart];
const VALUE_ACCENTS = ["#1FB6C1", "#0C2A40", "#1D6A8C", "#42954B", "#1D6A8C", "#1D6A8C", "#1FB6C1"];

export default function About() {
  return (
    <div data-testid="about-page">
      <SEO
        title="About"
        description="A South African water company serving 1,500+ sites across hospitality, mining, agriculture, estates and municipalities. Founded 2016. Specification-led from survey to SLA."
        path="/about"
      />

      {/* HERO */}
      <section className="eng-hero">
        <div className="eh-bg" aria-hidden><img src="/generated/hero-about.webp" alt="" loading="lazy" decoding="async"/></div>
        <div className="eh-veil" aria-hidden />
        <div className="eh-content">
          <motion.div initial="hidden" animate="show" variants={stagger}>
            <motion.div variants={fadeUp} className="eh-tag">Company Story</motion.div>
            <motion.h1 variants={fadeUp}>
              A South African<br />
              water company. <strong>One accountable<br />supplier</strong> per site.
            </motion.h1>
            <motion.p variants={fadeUp} className="eh-lede">
              Founded 2016 as a single-patent R&D effort. Now a multi-discipline water company
              serving sites across hospitality, mining, agriculture, estates and
              municipalities — under one signature, from survey to lifelong service.
            </motion.p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.4 }} className="cred-bar mt-12 md:mt-16" data-testid="at-a-glance">
            {AT_A_GLANCE.map((p) => (
              <div key={p.lbl} className="cred-cell">
                <div className="cred-val">{p.val}</div>
                <div className="cred-lbl">{p.lbl}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CAPABILITY MATRIX — removed per user feedback (already covered on Home + Solutions) */}

      {/* MILESTONES */}
      <section className="bg-white px-6 md:px-12 lg:px-20 py-14 md:py-20" data-testid="journey">
        <div className="max-w-[1500px] mx-auto">
          <h2 className="font-display mb-8 md:mb-10" style={{ fontSize: "clamp(1.5rem, 2.6vw, 2.2rem)", lineHeight: 1.15, letterSpacing: "-0.015em", fontWeight: 500, color: "#0C2A40" }}>
            A decade of water.
          </h2>

          <div className="relative">
            <div className="absolute left-0 right-0 top-[68px] h-px hidden md:block" style={{ background: "linear-gradient(90deg, transparent, rgba(43,192,207,0.45) 6%, rgba(43,192,207,0.45) 94%, transparent)" }} aria-hidden />
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={stagger} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 md:gap-4">
              {JOURNEY.map((j) => (
                <motion.div key={j.year} variants={fadeUp} className="relative pt-20" data-testid={`journey-${j.year}`}>
                  <div className="absolute top-[62px] left-0 w-3 h-3 rounded-full hidden md:block" style={{ background: "#1FB6C1", boxShadow: "0 0 12px rgba(43,192,207,0.6)" }} aria-hidden />
                  <div className="absolute top-0 left-0 font-display" style={{ fontSize: "clamp(1.6rem, 2.6vw, 2.2rem)", lineHeight: 1, letterSpacing: "-0.03em", color: "#070A0D" }}>
                    {j.year}
                  </div>
                  <div className="eyebrow text-[#1FB6C1] mb-2" style={{ fontSize: "0.58rem", letterSpacing: "0.22em" }}>{j.tag}</div>
                  <h3 className="font-industrial text-[0.95rem] md:text-[1rem] mb-2 leading-snug">{j.title}</h3>
                  <p className="text-[0.84rem] font-light text-black/65 leading-relaxed">{j.body}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* VISION + MISSION — light hybrid */}
      <section className="px-6 md:px-12 lg:px-20 py-14 md:py-20" style={{ background: "#FFFFFF", color: "#0C2A40" }}>
        <div className="max-w-[1400px] mx-auto">
          <h2 className="font-display mb-8 md:mb-10" style={{ fontSize: "clamp(1.5rem, 2.6vw, 2.2rem)", lineHeight: 1.15, letterSpacing: "-0.015em", fontWeight: 500, color: "#0C2A40" }}>
            Where we're going and why.
          </h2>

          <div className="grid md:grid-cols-2 gap-5 md:gap-6">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
              <div className="outcome-tile light" style={{ "--ot-accent": "#1FB6C1", padding: "2.2rem 2rem" }}>
                <div className="ot-icon"><Compass /></div>
                <div className="ot-eyebrow">Our Vision</div>
                <p className="font-display" style={{ fontSize: "clamp(1.15rem, 2vw, 1.6rem)", fontWeight: 300, lineHeight: 1.4, color: "#0C2A40" }}>
                  {BRAND.vision}
                </p>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.1 }}>
              <div className="outcome-tile light" style={{ "--ot-accent": "#42954B", padding: "2.2rem 2rem" }}>
                <div className="ot-icon"><Sprout /></div>
                <div className="ot-eyebrow" style={{ color: "#42954B" }}>Our Mission</div>
                <p className="font-display" style={{ fontSize: "clamp(1.15rem, 2vw, 1.6rem)", fontWeight: 300, lineHeight: 1.4, color: "#0C2A40" }}>
                  {BRAND.mission}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* VALUES — 7 outcome-tile principles with rotating colours + icons */}
      <section className="bg-white px-6 md:px-12 lg:px-20 py-14 md:py-20" data-testid="values">
        <div className="max-w-[1500px] mx-auto">
          <h2 className="font-display mb-8 md:mb-10" style={{ fontSize: "clamp(1.5rem, 2.6vw, 2.2rem)", lineHeight: 1.15, letterSpacing: "-0.015em", fontWeight: 500, color: "#0C2A40" }}>
            What we stand for.
          </h2>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={stagger} className="spec-grid cols-4">
            {BRAND.values.map(([name, copy], i) => {
              const Icon = VALUE_ICONS[i] || Award;
              const accent = VALUE_ACCENTS[i] || "#1FB6C1";
              return (
                <motion.div key={name} variants={fadeUp}>
                  <div className="outcome-tile light" style={{ "--ot-accent": accent }}>
                    <div className="ot-icon"><Icon /></div>
                    <div className="ot-title">{name}</div>
                    <p className="ot-lede">{copy}</p>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* STANDARDS */}
      <section className="bg-[#fbfbfa] py-10 md:py-12 px-6 md:px-12 lg:px-20 border-t" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
        <div className="max-w-[1500px] mx-auto flex flex-wrap items-center justify-between gap-6">
          <div className="eyebrow text-black/55" style={{ fontSize: "0.6rem", letterSpacing: "0.28em" }}>
            Affiliations · Standards · Bodies
          </div>
          <div className="badge-strip">
            {STANDARDS_BADGES.map((b) => <span key={b} className="badge light">{b}</span>)}
          </div>
        </div>
      </section>

      {/* CEO MESSAGE — verbatim from PDF (Dirk van Schalkwyk, page 23) */}
      <CEOMessage />

      {/* CTA */}
      <section className="cta-strip">
        <div className="csi">
          <div>
            <h3>Get in touch with the team.</h3>
            <p className="csi-meta">
              For personalised assistance and solutions, contact our specialists.
            </p>
          </div>
          <Link to="/contact" className="hv-magnetic" style={{ justifyContent: "center" }}>
            Contact us <span aria-hidden>→</span>
          </Link>
        </div>
      </section>
    </div>
  );
}
